import { useMemoizedFn } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Dialog, Form, Input, InputNumber, Select, Textarea } from 'tdesign-react';
import formRulePattern from '@/utils/formRulePattern';
import { deal70101, isEmpty, toTsOptions } from '@/utils/common';
import Tip from '@/components/Tip';
import { createTopic, getNamespacesOptions } from '@/services/topicService';
import Text from '@/components/Text';
import { intYesNoOptions } from '@/utils/constant';
import useDisableButton from '@/components/DisableWrapper/useDisableButton';

const { FormItem } = Form;

function FormModal({ handleClose, visible, clusterName, tenantOptions, ...others }) {
  const [form] = Form.useForm();
  const [nsOptions, setNsOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const isPartitioned = Form.useWatch('isPartitioned', form);
  const confirmBtn = useDisableButton({ loading: submitting, content: '确定' });

  const loadNSOptions = useMemoizedFn(async (tenantVal) => {
    const options = await getNamespacesOptions({
      clusterName,
      tenant: tenantVal,
    });
    setNsOptions(options);
  });

  // 切换tenant
  const tenantChange = (val) => {
    setNsOptions([]);
    form.setFields([{ name: 'namespace', value: null, status: '' }]);
    if (val) {
      loadNSOptions(val);
    }
  };

  useEffect(() => {
    if (visible) {
      form.reset();
    }
  }, [visible, form]);

  const doClose = () => handleClose();

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      setSubmitting(true);
      try {
        const { meta } = await createTopic(fields);

        // 主集群创建的资源已在灾备集群存在
        if (meta.code === 70101) {
          deal70101(meta.publicMessage, () => handleSubmit({ validateResult, fields }), {
            url: '/cluster/topic/list',
            tenant: fields.tenant,
            namespace: fields.namespace,
            topic: fields.topic,
          });
          return;
        }
        handleClose(true);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <Root
      header='创建Topic'
      onClose={doClose}
      onConfirm={() => form.submit()}
      confirmBtn={confirmBtn}
      visible={visible}
      width={660}
      placement='center'
      {...others}
    >
      <Form
        form={form}
        resetType='initial'
        onSubmit={handleSubmit}
        initialData={{ clusterName, isPartitioned: 1, messageTraceOpen: 0 }}
        labelWidth={160}
        style={{ width: '86%', paddingBottom: 15 }}
      >
        <FormItem name='clusterName' label='集群名称'>
          <Text />
        </FormItem>
        <FormItem name='tenant' label='租户' rules={[{ required: true }]}>
          <Select
            options={toTsOptions(tenantOptions)}
            onChange={tenantChange}
            filterable
            clearable
          />
        </FormItem>
        <FormItem name='namespace' label='命名空间' rules={[{ required: true }]}>
          <Select options={toTsOptions(nsOptions)} filterable clearable />
        </FormItem>
        <FormItem name='topic' label='Topic' rules={[{ required: true }, formRulePattern('name')]}>
          <Input clearable />
        </FormItem>
        <FormItem
          name='messageTraceOpen'
          label={
            <Tip content='请确保当前pulsar集群支持消息轨迹功能，否则该配置无法生效'>
              开启消息轨迹
            </Tip>
          }
          rules={[{ required: true }]}
        >
          <Select options={toTsOptions(intYesNoOptions)} />
        </FormItem>
        <FormItem name='isPartitioned' label='分区Topic' rules={[{ required: true }]}>
          <Select options={toTsOptions(intYesNoOptions)} />
        </FormItem>
        {isPartitioned === 1 && (
          <FormItem
            name='partitionNum'
            label='分区数量'
            rules={[
              { required: true },
              { validator: (val) => isEmpty(val) || val > 0, message: '请输入大于0的值' },
              { validator: (val) => isEmpty(val) || val <= 1000, message: '分区数量不超过1000个' },
            ]}
          >
            <InputNumber theme='normal' decimalPlaces={0} />
          </FormItem>
        )}
        <FormItem name='createUser' label='负责人'>
          <Input />
        </FormItem>
        <FormItem name='app' label='应用'>
          <Input />
        </FormItem>
        <FormItem name='note' label='备注'>
          <Textarea placeholder='请输入' />
        </FormItem>
      </Form>
    </Root>
  );
}

export default connect((state) => ({
  tenantOptions: state.topic.tenantOptions,
}))(FormModal);

const Root = styled(Dialog)`
  .t-input-number {
    width: auto;
    flex: 1;
  }
  .t-input__suffix {
    color: var(--td-font-gray-2);
  }
`;
